import clsx from "clsx";
import { uniqueId } from "lodash";
import React from "react";
import { LineItemTemplate, Membership } from "types";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { ProductImage } from "@/components/Common/ProductImage";
import { QuantitySpinner } from "@/components/Common/QuantitySpinner/QuantitySpinner";
import { GuestQuestionnaireList } from "@/components/PurchaseCreate/GuestQuestionnaire/GuestQuestionnaireList";
import { CreatePurchaseMembershipTerms } from "@/components/PurchaseCreate/Membership/CreatePurchaseMembershipTerms";
import { MemberIdentificationInfoAlert } from "@/components/PurchaseCreate/Membership/MemberIdentificationInfoAlert";
import { useResource } from "@/hooks/useResource";
import { getMembershipLineItems } from "@/models/PurchaseItem";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface CreatePurchaseMembershipFieldsProps {
    membership: Membership;
}

export const CreatePurchaseMembershipFields = ({ membership }: CreatePurchaseMembershipFieldsProps) => {
    const hasMembershipLineItem = useCreatePurchaseStore(
        (state) => getMembershipLineItems(state.getActiveItem())?.length > 0,
    );
    const hasAnyItems = useCreatePurchaseStore((state) => state.purchase.items.length > 0);
    const isOneStepCheckout = useCreatePurchaseStore((state) => state.isOneStepCheckout());

    if (!hasMembershipLineItem) return null;

    return (
        <div className="w-full space-y-8">
            <div className="space-y-4">
                <MembershipTile membership={membership} />
                <MembershipQuantitySpinner />
                <CreatePurchaseMembershipTerms membership={membership} />
            </div>

            {hasAnyItems && !isOneStepCheckout && (
                <>
                    <hr className="text-gray-light" />

                    <GuestQuestionnaireList />

                    <MemberIdentificationInfoAlert membership={membership} />
                </>
            )}
        </div>
    );
};

const MembershipTile = ({ membership }: { membership: Membership }) => {
    return (
        <div className="space-y-3 rounded bg-gray-lighter p-4">
            <div className="flex items-center space-x-1">
                <div className="flex">
                    <ProductImage product={membership} size="small" className="!mr-1" />
                </div>

                <NoTranslate as="h4" className="font-bold">
                    {membership.name}
                </NoTranslate>
            </div>
        </div>
    );
};

const MembershipQuantitySpinner = () => {
    const { templateId, quantity } = useCreatePurchaseStore((state) => {
        const lineItem = getMembershipLineItems(state.getActiveItem())?.[0];
        return lineItem
            ? { templateId: lineItem?.template.id, quantity: lineItem?.quantity }
            : { templateId: null, quantity: null };
    });

    const updateQuantity = useCreatePurchaseStore((state) => state.actions.updateQuantity);
    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);
    const isMist = useCreatePurchaseStore((state) => state.isMist());
    const preventQuantitySync = useCreatePurchaseStore((state) => state.preventQuantitySync);

    const { data: demographicTemplate } = useResource<LineItemTemplate>(["lineItemTemplates", templateId]);

    const popoverParentId = uniqueId("popoverParentId");
    const disabledSpinner = false;
    const error = false;

    const handleQuantityChange = (newQuantity: number) => {
        updateQuantity(activeItemIndex, templateId!, newQuantity, demographicTemplate);
    };

    if (!templateId || !quantity) {
        return null;
    }

    return (
        <div
            className="flex items-center justify-start"
            data-testid="create-purchase-demographic-picker-item"
            id={popoverParentId}
        >
            <QuantitySpinner
                value={quantity}
                total={quantity}
                min={1}
                size="large"
                classNames={{ fieldset: "mr-3", label: "font-bold" }}
                shouldSyncValue={!isMist && !preventQuantitySync}
                popoverParentId={popoverParentId}
                disabled={disabledSpinner}
                onChange={handleQuantityChange}
            />

            <div className="flex-1">
                <div className={clsx("font-bold", { "text-danger": error })}>{demographicTemplate?.name}</div>
            </div>
        </div>
    );
};
